import React from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Calendar, Clock, ArrowLeft, Tag } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { blogPosts } from '../data/blog'
import VideoPlayer from '../components/VideoPlayer'

const BlogPost = () => {
  const { id } = useParams()
  const post = blogPosts.find(p => p.id === parseInt(id))

  if (!post) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog" className="btn btn-primary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen pt-16">
      <article className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/blog" className="btn btn-ghost mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {post.image && (
            <div className="mb-8 rounded-lg overflow-hidden">
              {post.image.endsWith('.mp4') ? (
                <VideoPlayer src={post.image} className="h-96" />
              ) : (
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-96 object-cover"
                />
              )}
            </div>
          )}

          <div className="mb-8">
            {post.featured && (
              <span className="badge badge-primary mb-4">Featured</span>
            )}
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gradient">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-base-content/70 mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {post.readTime}
              </div>
              <span className="badge badge-outline">{post.category}</span>
            </div>
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                <Tag className="w-5 h-5 text-base-content/70" />
                {post.tags.map((tag) => (
                  <span key={tag} className="badge badge-outline">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="prose prose-lg max-w-none dark:prose-invert">
            <ReactMarkdown>{post.content}</ReactMarkdown>
          </div>

          <div className="mt-12 pt-8 border-t border-base-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-base-content/70 mb-2">Written by</p>
                <p className="font-semibold">{post.author}</p>
              </div>
              <Link to="/blog" className="btn btn-outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Link>
            </div>
          </div>
        </motion.div>
      </article>
    </div>
  )
}

export default BlogPost
